import React, { useState, useEffect } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { PROP_TYPES, decimalToAmerican } from '../../constants/sports';

function PlayerPropsView({ game, sportKey, betSlipLegs, onToggleLeg, onBack }) {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    let cancelled = false;
    async function fetchProps() {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable(functions, 'fetchPlayerProps');
        const result = await fn({ eventId: game.id, sport: sportKey });
        if (!cancelled) {
          setProps(result.data?.props || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load player props');
        }
      }
      if (!cancelled) setLoading(false);
    }
    fetchProps();
    return () => { cancelled = true; };
  }, [game.id, sportKey]);

  const isLegSelected = (athleteId, propType, selection) => {
    return betSlipLegs.some(l =>
      l.gameId === game.id &&
      l.market === 'player_prop' &&
      l.athleteId === athleteId &&
      l.propType === propType &&
      l.selection === selection
    );
  };

  const handlePropClick = (prop, selection) => {
    const odds = selection === 'over' ? prop.overOdds : prop.underOdds;
    if (!odds) return;
    onToggleLeg({
      gameId: game.id,
      sport: sportKey,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      commenceTime: game.commenceTime,
      market: 'player_prop',
      selection,
      odds,
      point: prop.line,
      athleteId: prop.athleteId,
      athleteName: prop.athleteName,
      propType: prop.type,
    });
  };

  const filteredProps = filterType === 'all'
    ? props
    : props.filter(p => p.type === filterType);

  const availableTypes = [...new Set(props.map(p => p.type))];

  // Group props by player
  const byPlayer = {};
  filteredProps.forEach(prop => {
    if (!byPlayer[prop.athleteId]) {
      byPlayer[prop.athleteId] = {
        name: prop.athleteName,
        team: prop.teamAbbrev,
        lines: [],
      };
    }
    byPlayer[prop.athleteId].lines.push(prop);
  });
  const players = Object.entries(byPlayer);

  const headerContent = (
    <div className="props-header">
      <button className="props-back-btn" onClick={onBack}>&larr; Back</button>
      <h3>{game.awayAbbrev} @ {game.homeAbbrev} &mdash; Player Props</h3>
    </div>
  );

  if (loading) {
    return <div className="player-props-view">{headerContent}<div className="sports-loading">Loading player props...</div></div>;
  }
  if (error) {
    return <div className="player-props-view">{headerContent}<div className="sports-empty">{error}</div></div>;
  }
  if (props.length === 0) {
    return <div className="player-props-view">{headerContent}<div className="sports-empty">No player props available for this game yet.</div></div>;
  }

  return (
    <div className="player-props-view">
      {headerContent}

      <div className="props-filter-tabs">
        <button
          className={`props-filter-btn ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >All</button>
        {availableTypes.map(type => (
          <button
            key={type}
            className={`props-filter-btn ${filterType === type ? 'active' : ''}`}
            onClick={() => setFilterType(type)}
          >{PROP_TYPES[type]?.short || type}</button>
        ))}
      </div>

      <div className="props-players">
        {players.map(([athleteId, player]) => (
          <div key={athleteId} className="props-player-section">
            <div className="props-player-header">
              <span className="props-player-name">{player.name}</span>
              <span className="props-player-team">{player.team}</span>
            </div>
            <div className="props-player-lines">
              {player.lines.map(prop => (
                <div key={`${prop.type}-${prop.line}`} className="props-line">
                  <span className="props-line-stat">{PROP_TYPES[prop.type]?.short || prop.type}</span>
                  <span className="props-line-num">{prop.line}</span>
                  <div className="props-line-btns">
                    <button
                      className={`props-odds-btn ${isLegSelected(prop.athleteId, prop.type, 'over') ? 'props-odds-selected' : ''}`}
                      onClick={() => handlePropClick(prop, 'over')}
                      disabled={!prop.overOdds}
                    >
                      <span className="props-odds-dir">O</span>
                      <span className="props-odds-value">{prop.overOdds ? decimalToAmerican(prop.overOdds) : '-'}</span>
                    </button>
                    <button
                      className={`props-odds-btn ${isLegSelected(prop.athleteId, prop.type, 'under') ? 'props-odds-selected' : ''}`}
                      onClick={() => handlePropClick(prop, 'under')}
                      disabled={!prop.underOdds}
                    >
                      <span className="props-odds-dir">U</span>
                      <span className="props-odds-value">{prop.underOdds ? decimalToAmerican(prop.underOdds) : '-'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PlayerPropsView;
